import React from "react";
import { useMutation } from "@apollo/client";
import { withTranslation } from "react-i18next";
import { withRouter } from "react-router";
import { toast } from "react-toastify";

import { Button, Badge, Icon } from "tabler-react";

import { JOIN_WAITLIST_MUTATION, CANCEL_WAITLIST_MUTATION } from "./queries";

function WaitlistButton({
    t,
    match,
    history,
    scheduleClass,
    classDate,
    scheduleItemId,
    bookingStatus,
}) {
    const [joinWaitlist, { loading: joinLoading }] = useMutation(
        JOIN_WAITLIST_MUTATION,
    );
    const [cancelWaitlist, { loading: cancelLoading }] = useMutation(
        CANCEL_WAITLIST_MUTATION,
    );

    const {
        userWaitlistPosition,
        waitlistCount,
        isUserOnWaitlist,
        canJoinWaitlist,
        userWaitlistAttendanceId,
        userBookingStatus,
        userAttendanceId,
    } = scheduleClass;

    const handleJoinWaitlist = async () => {
        try {
            await joinWaitlist({
                variables: {
                    input: {
                        scheduleItem: scheduleItemId,
                        date: classDate,
                        attendanceType: "CLASSPASS", // Default to classpass, could be made configurable
                    },
                },
            });

            toast.success(t("shop.classes.waitlist.join_success"), {
                position: toast.POSITION.BOTTOM_RIGHT,
            });

            // Refresh the page to update the UI
            window.location.reload();
        } catch (error) {
            toast.error(t("general.toast_server_error") + " " + error.message, {
                position: toast.POSITION.BOTTOM_RIGHT,
            });
            console.error("Error joining waitlist:", error);
        }
    };

    const handleLeaveWaitlist = async () => {
        try {
            await cancelWaitlist({
                variables: {
                    input: {
                        scheduleItemAttendance:
                            userWaitlistAttendanceId || userAttendanceId,
                    },
                },
            });

            toast.success(t("shop.classes.waitlist.leave_success"), {
                position: toast.POSITION.BOTTOM_RIGHT,
            });

            // Refresh the page to update the UI
            window.location.reload();
        } catch (error) {
            toast.error(t("general.toast_server_error") + " " + error.message, {
                position: toast.POSITION.BOTTOM_RIGHT,
            });
            console.error("Error leaving waitlist:", error);
        }
    };

    if (joinLoading || cancelLoading) {
        return (
            <div className="waitlist-loading">
                <Button disabled size="sm" color="secondary">
                    {t("general.please_wait")}
                </Button>
            </div>
        );
    }

    if (
        (isUserOnWaitlist || userBookingStatus === "WAITLISTED") &&
        ["OK", "FULL"].includes(bookingStatus)
    ) {
        return (
            <div className="waitlist-status">
                <Badge color="warning" className="mb-2">
                    {t("shop.classes.waitlist.position", {
                        position: userWaitlistPosition,
                    })}
                </Badge>
                <br />
                <Button
                    size="sm"
                    color="secondary"
                    outline
                    onClick={handleLeaveWaitlist}
                >
                    {t("shop.classes.waitlist.leave")}
                </Button>
            </div>
        );
    }

    if (
        canJoinWaitlist &&
        (!userBookingStatus || userBookingStatus === "CANCELLED_WAITLIST") &&
        ["OK", "FULL"].includes(bookingStatus)
    ) {
        return (
            <div className="waitlist-join">
                <div className="mb-2 text-muted">
                    {t("shop.classes.waitlist.class_full", {
                        count: waitlistCount,
                    })}
                </div>
                <Button color="primary" outline onClick={handleJoinWaitlist}>
                    {t("shop.classes.waitlist.join")}{" "}
                    <Icon name="chevron-right" />
                </Button>
            </div>
        );
    }
    var statusText = "";
    switch (bookingStatus) {
        case "CANCELLED":
            statusText = (
                <span className="pull-right">
                    {t("shop.classes.class_booking_status.cancelled")}
                </span>
            );
            break;
        case "HOLIDAY":
            statusText = (
                <span className="pull-right">
                    {t("shop.classes.class_booking_status.cancelled_holiday")}
                </span>
            );
            break;
        case "FINISHED":
            statusText = (
                <span className="pull-right">
                    {t("shop.classes.class_booking_status.finished")}
                </span>
            );
            break;
        case "ONGOING":
            statusText = (
                <span className="pull-right">
                    {t("shop.classes.class_booking_status.ongoing")}
                </span>
            );
            break;
        default:
            statusText = "";
    }
    return statusText;
}

export default withTranslation()(withRouter(WaitlistButton));
